import { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate, useLocation } from "react-router-dom";
import hotelImg from "../assests/images/hotel-bg.jpg";
import Hero from "../components/Hero";

import {
    FaHeart,
    FaRegHeart,
    FaMapMarkerAlt,
    FaStar,
    FaStarHalfAlt,
    FaRegStar,
    FaSearch,
    FaMoneyBillWave,
    FaMapMarkedAlt,
    FaUndo,
    FaSlidersH,
    FaChevronDown,
    FaArrowRight,
    FaSwimmingPool,
    FaWifi,
    FaUtensils,
    FaHotel,
    FaCheck,
    FaTimes,
} from "react-icons/fa";

export default function HotelList({
    limit,
    showViewAllButton = false,
    showSidebar = true,
    showBanner = false,
    showHeading = false,
}) {
    const [hotels, setHotels] = useState([]);
    const [loading, setLoading] = useState(true);

    const location = useLocation();
    const navigate = useNavigate();

    // ============================================================
    // WISHLIST
    // ============================================================

    const [wishlist, setWishlist] = useState([]);

    // ============================================================
    // FILTERS
    // ============================================================

    const [search, setSearch] = useState("");
    const [minPrice, setMinPrice] = useState(0);
    const [maxPrice, setMaxPrice] = useState(5000);
    const [rating, setRating] = useState(0);

    const [destinations, setDestinations] = useState([]);
    const [selectedDestinations, setSelectedDestinations] =
        useState([]);

    const [debouncedSearch, setDebouncedSearch] =
        useState(search);

    // ============================================================
    // MOBILE FILTERS
    // ============================================================

    const [mobileFiltersOpen, setMobileFiltersOpen] =
        useState(false);

    // ============================================================
    // LOAD WISHLIST
    // ============================================================

    useEffect(() => {
        const storedWishlist =
            JSON.parse(localStorage.getItem("wishlist")) || [];

        setWishlist(storedWishlist);
    }, []);

    // ============================================================
    // UPDATE WISHLIST
    // ============================================================

    const updateWishlist = (updated) => {
        setWishlist(updated);

        localStorage.setItem(
            "wishlist",
            JSON.stringify(updated)
        );
    };

    // ============================================================
    // TOGGLE WISHLIST
    // ============================================================

    const toggleWishlist = (hotel) => {
        const exists = wishlist.find(
            (item) => item.id === hotel.id
        );

        let updated;

        if (exists) {
            updated = wishlist.filter(
                (item) => item.id !== hotel.id
            );
        } else {
            updated = [...wishlist, hotel];
        }

        updateWishlist(updated);
    };

    // ============================================================
    // CHECK WISHLIST
    // ============================================================

    const isWishlisted = (id) => {
        return wishlist.some((item) => item.id === id);
    };

    // ============================================================
    // FETCH DESTINATIONS
    // ============================================================

    const fetchDestinations = async () => {
        try {
            const res = await axios.get(
                "https://marinadubai.com/api/destinations"
            );

            setDestinations(res.data);
        } catch (err) {
            console.error(err);
        }
    };

    useEffect(() => {
        fetchDestinations();
    }, []);

    // ============================================================
    // DESTINATION FILTER
    // ============================================================

    const handleDestinationChange = (dest) => {
        setSelectedDestinations((prev) =>
            prev.some((d) => d.id === dest.id)
                ? prev.filter((d) => d.id !== dest.id)
                : [...prev, dest]
        );
    };

    // ============================================================
    // DEBOUNCE SEARCH
    // ============================================================

    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedSearch(search);
        }, 500);

        return () => clearTimeout(handler);
    }, [search]);

    // ============================================================
    // FETCH HOTELS
    // ============================================================

    const fetchHotels = async () => {
        setLoading(true);

        try {
            const res = await axios.get(
                "https://marinadubai.com/api/hotels",
                {
                    params: {
                        search: debouncedSearch,
                        destinations:
                            selectedDestinations.map(
                                (d) => d.id
                            ),
                        minPrice,
                        maxPrice,
                        rating,
                    },
                }
            );

            setHotels(res.data);
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchHotels();
    }, [
        debouncedSearch,
        minPrice,
        maxPrice,
        rating,
        selectedDestinations,
    ]);

    // ============================================================
    // STAR RATING
    // ============================================================

    const renderStars = (ratingValue) => {
        return Array.from({ length: 5 }, (_, i) => {
            if (i < Math.floor(ratingValue)) {
                return <FaStar key={i} />;
            }

            if (
                i === Math.floor(ratingValue) &&
                ratingValue % 1 >= 0.5
            ) {
                return <FaStarHalfAlt key={i} />;
            }

            return <FaRegStar key={i} />;
        });
    };

    // ============================================================
    // FILTER STATE
    // ============================================================

    const isFilterChanged =
        search !== "" ||
        minPrice !== 0 ||
        maxPrice !== 5000 ||
        rating !== 0 ||
        selectedDestinations.length > 0;

    // ============================================================
    // RESET FILTERS
    // ============================================================

    const resetFilters = () => {
        setSearch("");
        setMinPrice(0);
        setMaxPrice(5000);
        setRating(0);
        setSelectedDestinations([]);
    };

    // ============================================================
    // ACTIVE FILTER COUNT
    // ============================================================

    const activeFilterCount =
        (search ? 1 : 0) +
        (minPrice !== 0 || maxPrice !== 5000 ? 1 : 0) +
        (rating !== 0 ? 1 : 0) +
        (selectedDestinations.length > 0 ? 1 : 0);

    return (
        <div className="min-h-screen bg-[#F7F5EF]">

            {/* ==================================================
                HERO
            ================================================== */}

            {showBanner && (
                <div className="relative">

                    <Hero
                        variant="inner"
                        title="Find Your Perfect Hotel"
                        subtitle="Discover exceptional stays across Dubai Marina"
                        backgroundImage={hotelImg}
                    />

                    {/* Floating Search */}
                    <div className="relative z-20 mx-auto -mt-10 max-w-6xl px-4">
                        <div className="rounded-[22px] border border-[#DED8CC] bg-[#FBFAF6] p-3 shadow-[0_20px_60px_rgba(23,63,62,0.14)]">

                            <div className="flex flex-col gap-3 md:flex-row">

                                <div className="relative flex-1">

                                    <FaSearch className="absolute left-5 top-1/2 -translate-y-1/2 text-[#0B5552]" />

                                    <input
                                        type="text"
                                        value={search}
                                        onChange={(e) =>
                                            setSearch(
                                                e.target.value
                                            )
                                        }
                                        placeholder="Search hotels, areas or destinations..."
                                        className="
                                            w-full
                                            rounded-[15px]
                                            border
                                            border-[#DED8CC]
                                            bg-[#F7F5EF]
                                            py-4
                                            pl-12
                                            pr-5
                                            text-sm
                                            font-medium
                                            text-[#173F3E]
                                            outline-none
                                            transition-all
                                            duration-300
                                            placeholder:text-[#8A9490]
                                            focus:border-[#B78932]
                                            focus:bg-white
                                            focus:ring-4
                                            focus:ring-[#B78932]/10
                                        "
                                    />

                                </div>

                                {/* Mobile Filter */}
                                <button
                                    onClick={() =>
                                        setMobileFiltersOpen(
                                            true
                                        )
                                    }
                                    className="
                                        flex
                                        items-center
                                        justify-center
                                        gap-2
                                        rounded-[15px]
                                        bg-[#173F3E]
                                        px-6
                                        py-4
                                        text-sm
                                        font-bold
                                        text-white
                                        transition-all
                                        duration-300
                                        hover:bg-[#0B5552]
                                        md:hidden
                                    "
                                >
                                    <FaSlidersH />

                                    Filters

                                    {activeFilterCount > 0 && (
                                        <span
                                            className="
                                                flex
                                                h-5
                                                w-5
                                                items-center
                                                justify-center
                                                rounded-full
                                                bg-[#D5A33C]
                                                text-[10px]
                                                font-bold
                                                text-[#173F3E]
                                            "
                                        >
                                            {activeFilterCount}
                                        </span>
                                    )}
                                </button>

                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* ==================================================
                MAIN CONTAINER
            ================================================== */}

            <div
                className={`
                    mx-auto
                    max-w-[1500px]
                    px-4
                    sm:px-6
                    lg:px-8
                    ${showBanner
                        ? "pb-16 pt-12"
                        : "py-10 sm:py-14 lg:py-16"
                    }
                `}
            >

                {/* ==================================================
                    PAGE HEADING
                ================================================== */}

                {(showHeading ||
                    location.pathname === "/hotels") && (
                        <div className="mb-10">

                            <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">

                                <div>

                                    {/* Eyebrow */}

                                    <div
                                        className="
                                        mb-4
                                        flex
                                        items-center
                                        gap-3
                                    "
                                    >
                                        <span className="h-px w-8 bg-[#D5A33C]" />

                                        <span
                                            className="
                                            text-[10px]
                                            font-semibold
                                            uppercase
                                            tracking-[0.22em]
                                            text-[#0B5552]
                                        "
                                        >
                                            Dubai Marina Collection
                                        </span>
                                    </div>

                                    {/* Heading */}

                                    <h1
                                        className="
                                        font-serif
                                        text-[34px]
                                        font-medium
                                        leading-[1.08]
                                        tracking-[-0.02em]
                                        text-[#173F3E]

                                        sm:text-[42px]

                                        lg:text-[48px]
                                    "
                                    >
                                        Find Your Perfect
                                        <span
                                            className="
                                            block
                                            italic
                                            text-[#B78932]
                                        "
                                        >
                                            Stay in Dubai
                                        </span>
                                    </h1>

                                    <p
                                        className="
                                        mt-4
                                        max-w-2xl
                                        text-sm
                                        leading-7
                                        text-[#687572]

                                        sm:text-base
                                    "
                                    >
                                        Explore exceptional hotels,
                                        luxury resorts and beautiful
                                        stays across Dubai's most
                                        desirable destinations.
                                    </p>

                                </div>

                                {/* Hotel Count */}

                                <div
                                    className="
                                    rounded-[15px]
                                    border
                                    border-[#DED8CC]
                                    bg-[#FBFAF6]
                                    px-5
                                    py-3
                                    shadow-[0_8px_25px_rgba(23,63,62,0.05)]
                                "
                                >
                                    <span
                                        className="
                                        font-serif
                                        text-2xl
                                        font-medium
                                        text-[#B78932]
                                    "
                                    >
                                        {hotels.length}
                                    </span>

                                    <span
                                        className="
                                        ml-2
                                        text-xs
                                        font-semibold
                                        text-[#687572]
                                    "
                                    >
                                        Hotels Available
                                    </span>
                                </div>

                            </div>

                            {/* Results Summary */}

                            <div className="mt-8 flex flex-wrap items-center gap-3">

                                {selectedDestinations.length > 0 && (
                                    <div
                                        className="
                                        flex
                                        items-center
                                        gap-2
                                        rounded-full
                                        border
                                        border-[#D5A33C]/30
                                        bg-[#F3EBDD]
                                        px-4
                                        py-2.5
                                        text-xs
                                        font-semibold
                                        text-[#0B5552]
                                    "
                                    >
                                        <FaMapMarkerAlt />

                                        {selectedDestinations.length}{" "}
                                        destinations
                                    </div>
                                )}

                                {rating > 0 && (
                                    <div
                                        className="
                                        flex
                                        items-center
                                        gap-2
                                        rounded-full
                                        border
                                        border-[#D5A33C]/30
                                        bg-[#F3EBDD]
                                        px-4
                                        py-2.5
                                        text-xs
                                        font-semibold
                                        text-[#8A6900]
                                    "
                                    >
                                        <FaStar />

                                        {rating}+ rating
                                    </div>
                                )}

                                {isFilterChanged && (
                                    <button
                                        onClick={resetFilters}
                                        className="
                                        flex
                                        items-center
                                        gap-2
                                        rounded-full
                                        px-4
                                        py-2.5
                                        text-xs
                                        font-semibold
                                        text-[#687572]
                                        transition
                                        hover:bg-white
                                        hover:text-[#A85A4A]
                                    "
                                    >
                                        <FaTimes />

                                        Clear filters
                                    </button>
                                )}

                            </div>

                        </div>
                    )}

                {/* ==================================================
                    MOBILE FILTER BUTTON
                ================================================== */}

                {!showBanner && (
                    <div className="mb-6 md:hidden">

                        <button
                            onClick={() =>
                                setMobileFiltersOpen(true)
                            }
                            className="
                                flex
                                w-full
                                items-center
                                justify-between
                                rounded-[18px]
                                border
                                border-[#DED8CC]
                                bg-[#FBFAF6]
                                px-5
                                py-4
                                shadow-[0_6px_20px_rgba(23,63,62,0.05)]
                            "
                        >

                            <span
                                className="
                                    flex
                                    items-center
                                    gap-3
                                    text-sm
                                    font-bold
                                    text-[#173F3E]
                                "
                            >

                                <span
                                    className="
                                        flex
                                        h-9
                                        w-9
                                        items-center
                                        justify-center
                                        rounded-xl
                                        bg-[#F3EBDD]
                                        text-[#0B5552]
                                    "
                                >
                                    <FaSlidersH />
                                </span>

                                Filter Hotels

                            </span>

                            <span className="flex items-center gap-2">

                                {activeFilterCount > 0 && (
                                    <span
                                        className="
                                            flex
                                            h-6
                                            min-w-6
                                            items-center
                                            justify-center
                                            rounded-full
                                            bg-[#D5A33C]
                                            px-1.5
                                            text-[10px]
                                            font-black
                                            text-[#173F3E]
                                        "
                                    >
                                        {activeFilterCount}
                                    </span>
                                )}

                                <FaChevronDown className="text-xs text-[#687572]" />

                            </span>

                        </button>

                    </div>
                )}

                {/* ==================================================
                    MAIN LAYOUT
                ================================================== */}

                <div className="flex flex-col gap-8 lg:flex-row">

                    {/* ==================================================
                        SIDEBAR
                    ================================================== */}

                    {showSidebar && (
                        <aside
                            className={`
                                fixed
                                inset-0
                                z-[100]

                                lg:relative
                                lg:inset-auto
                                lg:z-auto

                                ${mobileFiltersOpen
                                    ? "block"
                                    : "hidden"
                                }

                                lg:block
                                lg:w-[290px]
                                lg:flex-shrink-0
                            `}
                        >

                            {/* Mobile Overlay */}

                            <div
                                onClick={() =>
                                    setMobileFiltersOpen(false)
                                }
                                className="
                                    absolute
                                    inset-0
                                    bg-[#173F3E]/60
                                    backdrop-blur-sm
                                    lg:hidden
                                "
                            />

                            <div
                                className="
                                    absolute
                                    bottom-0
                                    left-0
                                    top-0
                                    w-[88%]
                                    overflow-y-auto
                                    bg-[#FBFAF6]
                                    shadow-2xl

                                    sm:w-[360px]

                                    lg:relative
                                    lg:top-0
                                    lg:w-full
                                    lg:overflow-visible
                                    lg:rounded-[22px]
                                    lg:border
                                    lg:border-[#DED8CC]
                                    lg:shadow-[0_10px_35px_rgba(23,63,62,0.06)]
                                "
                            >

                                {/* Filter Header */}

                                <div
                                    className="
                                        sticky
                                        top-0
                                        z-10
                                        flex
                                        items-center
                                        justify-between
                                        border-b
                                        border-[#DED8CC]
                                        bg-[#FBFAF6]
                                        px-5
                                        py-5

                                        lg:rounded-t-[22px]
                                    "
                                >

                                    <div>

                                        <div className="flex items-center gap-3">

                                            <span
                                                className="
                                                    flex
                                                    h-10
                                                    w-10
                                                    items-center
                                                    justify-center
                                                    rounded-xl
                                                    bg-[#173F3E]
                                                    text-[#D5A33C]
                                                "
                                            >
                                                <FaSlidersH />
                                            </span>

                                            <div>

                                                <h3
                                                    className="
                                                        font-serif
                                                        text-lg
                                                        font-medium
                                                        text-[#173F3E]
                                                    "
                                                >
                                                    Filter Hotels
                                                </h3>

                                                <p
                                                    className="
                                                        mt-0.5
                                                        text-[11px]
                                                        text-[#687572]
                                                    "
                                                >
                                                    Find your ideal stay
                                                </p>

                                            </div>

                                        </div>

                                    </div>

                                    <button
                                        onClick={() =>
                                            setMobileFiltersOpen(
                                                false
                                            )
                                        }
                                        className="
                                            flex
                                            h-9
                                            w-9
                                            items-center
                                            justify-center
                                            rounded-xl
                                            bg-[#F3EBDD]
                                            text-[#687572]
                                            transition
                                            hover:bg-[#173F3E]
                                            hover:text-white

                                            lg:hidden
                                        "
                                    >
                                        <FaTimes />
                                    </button>

                                </div>

                                <div className="p-5">

                                    {/* ==================================================
                                        SEARCH
                                    ================================================== */}

                                    <div className="mb-7">

                                        <label
                                            className="
                                                mb-3
                                                block
                                                text-[10px]
                                                font-semibold
                                                uppercase
                                                tracking-[0.18em]
                                                text-[#687572]
                                            "
                                        >
                                            Search
                                        </label>

                                        <div className="relative">

                                            <FaSearch
                                                className="
                                                    absolute
                                                    left-4
                                                    top-1/2
                                                    -translate-y-1/2
                                                    text-[#0B5552]
                                                "
                                            />

                                            <input
                                                type="text"
                                                value={search}
                                                onChange={(e) =>
                                                    setSearch(
                                                        e.target.value
                                                    )
                                                }
                                                placeholder="Hotel or destination"
                                                className="
                                                    w-full
                                                    rounded-[15px]
                                                    border
                                                    border-[#DED8CC]
                                                    bg-[#F7F5EF]
                                                    py-3.5
                                                    pl-11
                                                    pr-4
                                                    text-sm
                                                    font-medium
                                                    text-[#173F3E]
                                                    outline-none
                                                    transition
                                                    focus:border-[#B78932]
                                                    focus:bg-white
                                                    focus:ring-4
                                                    focus:ring-[#B78932]/10
                                                "
                                            />

                                        </div>

                                    </div>

                                    {/* ==================================================
                                        PRICE
                                    ================================================== */}

                                    <div
                                        className="
                                            mb-7
                                            border-t
                                            border-[#DED8CC]
                                            pt-6
                                        "
                                    >

                                        <div className="mb-3 flex items-center justify-between">

                                            <label
                                                className="
                                                    text-[10px]
                                                    font-semibold
                                                    uppercase
                                                    tracking-[0.18em]
                                                    text-[#687572]
                                                "
                                            >
                                                Price Range
                                            </label>

                                            <FaMoneyBillWave
                                                className="text-sm text-[#B78932]"
                                            />

                                        </div>

                                        <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-2">

                                            <div className="relative">

                                                <span
                                                    className="
                                                        absolute
                                                        left-3
                                                        top-1/2
                                                        -translate-y-1/2
                                                        text-[9px]
                                                        font-bold
                                                        text-[#8A9490]
                                                    "
                                                >
                                                    AED
                                                </span>

                                                <input
                                                    type="number"
                                                    value={minPrice}
                                                    onChange={(e) =>
                                                        setMinPrice(
                                                            e.target.value
                                                        )
                                                    }
                                                    placeholder="Min"
                                                    className="
                                                        w-full
                                                        rounded-xl
                                                        border
                                                        border-[#DED8CC]
                                                        bg-[#F7F5EF]
                                                        py-3
                                                        pl-11
                                                        pr-2
                                                        text-xs
                                                        font-bold
                                                        text-[#173F3E]
                                                        outline-none
                                                        transition
                                                        focus:border-[#B78932]
                                                        focus:bg-white
                                                    "
                                                />

                                            </div>

                                            <span className="text-[#B9BFBB]">
                                                —
                                            </span>

                                            <div className="relative">

                                                <span
                                                    className="
                                                        absolute
                                                        left-3
                                                        top-1/2
                                                        -translate-y-1/2
                                                        text-[9px]
                                                        font-bold
                                                        text-[#8A9490]
                                                    "
                                                >
                                                    AED
                                                </span>

                                                <input
                                                    type="number"
                                                    value={maxPrice}
                                                    onChange={(e) =>
                                                        setMaxPrice(
                                                            e.target.value
                                                        )
                                                    }
                                                    placeholder="Max"
                                                    className="
                                                        w-full
                                                        rounded-xl
                                                        border
                                                        border-[#DED8CC]
                                                        bg-[#F7F5EF]
                                                        py-3
                                                        pl-11
                                                        pr-2
                                                        text-xs
                                                        font-bold
                                                        text-[#173F3E]
                                                        outline-none
                                                        transition
                                                        focus:border-[#B78932]
                                                        focus:bg-white
                                                    "
                                                />

                                            </div>

                                        </div>

                                    </div>

                                    {/* ==================================================
                                        RATING
                                    ================================================== */}

                                    <div
                                        className="
                                            mb-7
                                            border-t
                                            border-[#DED8CC]
                                            pt-6
                                        "
                                    >

                                        <div className="mb-3 flex items-center justify-between">

                                            <label
                                                className="
                                                    text-[10px]
                                                    font-semibold
                                                    uppercase
                                                    tracking-[0.18em]
                                                    text-[#687572]
                                                "
                                            >
                                                Hotel Rating
                                            </label>

                                            <FaStar className="text-sm text-[#D5A33C]" />

                                        </div>

                                        <div className="relative">

                                            <select
                                                value={rating}
                                                onChange={(e) =>
                                                    setRating(
                                                        e.target.value
                                                    )
                                                }
                                                className="
                                                    w-full
                                                    appearance-none
                                                    rounded-[15px]
                                                    border
                                                    border-[#DED8CC]
                                                    bg-[#F7F5EF]
                                                    px-4
                                                    py-3.5
                                                    pr-10
                                                    text-sm
                                                    font-semibold
                                                    text-[#173F3E]
                                                    outline-none
                                                    transition
                                                    focus:border-[#B78932]
                                                    focus:bg-white
                                                "
                                            >

                                                <option value="0">
                                                    All Ratings
                                                </option>

                                                <option value="3">
                                                    3★ & above
                                                </option>

                                                <option value="4">
                                                    4★ & above
                                                </option>

                                                <option value="4.5">
                                                    4.5★ & above
                                                </option>

                                            </select>

                                            <FaChevronDown
                                                className="
                                                    pointer-events-none
                                                    absolute
                                                    right-4
                                                    top-1/2
                                                    -translate-y-1/2
                                                    text-[10px]
                                                    text-[#687572]
                                                "
                                            />

                                        </div>

                                    </div>

                                    {/* ==================================================
                                        DESTINATIONS
                                    ================================================== */}

                                    <div
                                        className="
                                            border-t
                                            border-[#DED8CC]
                                            pt-6
                                        "
                                    >

                                        <div className="mb-4 flex items-center justify-between">

                                            <label
                                                className="
                                                    flex
                                                    items-center
                                                    gap-2
                                                    text-[10px]
                                                    font-semibold
                                                    uppercase
                                                    tracking-[0.18em]
                                                    text-[#687572]
                                                "
                                            >

                                                <FaMapMarkedAlt className="text-[#0B5552]" />

                                                Destinations

                                            </label>

                                            {selectedDestinations.length >
                                                0 && (
                                                    <span
                                                        className="
                                                        rounded-full
                                                        bg-[#173F3E]
                                                        px-2.5
                                                        py-1
                                                        text-[9px]
                                                        font-black
                                                        text-[#D5A33C]
                                                    "
                                                    >
                                                        {
                                                            selectedDestinations.length
                                                        }
                                                    </span>
                                                )}

                                        </div>

                                        <div
                                            className="
                                                max-h-60
                                                space-y-1.5
                                                overflow-y-auto
                                                pr-1
                                            "
                                        >

                                            {destinations.map(
                                                (dest) => {

                                                    const isChecked =
                                                        selectedDestinations.some(
                                                            (d) =>
                                                                d.id ===
                                                                dest.id
                                                        );

                                                    return (
                                                        <label
                                                            key={
                                                                dest.id
                                                            }
                                                            className={`
                                                                flex
                                                                cursor-pointer
                                                                items-center
                                                                justify-between
                                                                gap-3
                                                                rounded-xl
                                                                border
                                                                px-3
                                                                py-3
                                                                transition-all

                                                                ${isChecked
                                                                    ? "border-[#B78932] bg-[#F3EBDD]"
                                                                    : "border-transparent bg-[#F7F5EF] hover:border-[#DED8CC] hover:bg-white"
                                                                }
                                                            `}
                                                        >

                                                            <div className="flex min-w-0 items-center gap-3">

                                                                <span
                                                                    className={`
                                                                        flex
                                                                        h-7
                                                                        w-7
                                                                        flex-shrink-0
                                                                        items-center
                                                                        justify-center
                                                                        rounded-lg

                                                                        ${isChecked
                                                                            ? "bg-[#173F3E] text-[#D5A33C]"
                                                                            : "bg-white text-[#C8CECA]"
                                                                        }
                                                                    `}
                                                                >
                                                                    {isChecked && (
                                                                        <FaCheck className="text-[9px]" />
                                                                    )}
                                                                </span>

                                                                <span
                                                                    className={`
                                                                        truncate
                                                                        text-xs
                                                                        font-semibold

                                                                        ${isChecked
                                                                            ? "text-[#173F3E]"
                                                                            : "text-[#687572]"
                                                                        }
                                                                    `}
                                                                >
                                                                    {
                                                                        dest.name
                                                                    }
                                                                </span>

                                                            </div>

                                                            <input
                                                                type="checkbox"
                                                                checked={
                                                                    isChecked
                                                                }
                                                                onChange={() =>
                                                                    handleDestinationChange(
                                                                        dest
                                                                    )
                                                                }
                                                                className="sr-only"
                                                            />

                                                        </label>
                                                    );
                                                }
                                            )}

                                        </div>

                                    </div>

                                    {/* ==================================================
                                        RESET
                                    ================================================== */}

                                    {isFilterChanged && (
                                        <div
                                            className="
                                                mt-7
                                                border-t
                                                border-[#DED8CC]
                                                pt-6
                                            "
                                        >

                                            <button
                                                onClick={
                                                    resetFilters
                                                }
                                                className="
                                                    flex
                                                    w-full
                                                    items-center
                                                    justify-center
                                                    gap-2
                                                    rounded-[15px]
                                                    border
                                                    border-[#DED8CC]
                                                    bg-white
                                                    py-3.5
                                                    text-xs
                                                    font-bold
                                                    text-[#687572]
                                                    transition-all
                                                    duration-300
                                                    hover:border-[#B78932]
                                                    hover:bg-[#F3EBDD]
                                                    hover:text-[#173F3E]
                                                "
                                            >
                                                <FaUndo />

                                                Reset All Filters
                                            </button>

                                        </div>
                                    )}

                                </div>
                            </div>
                        </aside>
                    )}

                    {/* ==================================================
                        HOTEL RESULTS
                    ================================================== */}

                    <main className="min-w-0 flex-1">

                        {/* Top Results Bar */}

                        <div
                            className="
                                mb-6
                                flex
                                flex-col
                                gap-4

                                sm:flex-row
                                sm:items-center
                                sm:justify-between
                            "
                        >

                            <div>

                                <p
                                    className="
                                        text-[10px]
                                        font-semibold
                                        uppercase
                                        tracking-[0.2em]
                                        text-[#687572]
                                    "
                                >
                                    Hotel Collection
                                </p>

                                <h2
                                    className="
                                        mt-1
                                        font-serif
                                        text-2xl
                                        font-medium
                                        text-[#173F3E]

                                        sm:text-3xl
                                    "
                                >
                                    {hotels.length}{" "}

                                    <span
                                        className="
                                            font-sans
                                            text-sm
                                            font-medium
                                            text-[#687572]
                                        "
                                    >
                                        stays found
                                    </span>
                                </h2>

                            </div>

                            {isFilterChanged && (
                                <div className="flex items-center gap-2">

                                    <span
                                        className="
                                            rounded-full
                                            border
                                            border-[#D5A33C]/30
                                            bg-[#F3EBDD]
                                            px-3
                                            py-1.5
                                            text-[10px]
                                            font-bold
                                            text-[#0B5552]
                                        "
                                    >
                                        {activeFilterCount} active
                                        filter
                                        {activeFilterCount > 1
                                            ? "s"
                                            : ""}
                                    </span>

                                    <button
                                        onClick={resetFilters}
                                        className="
                                            text-xs
                                            font-semibold
                                            text-[#687572]
                                            transition
                                            hover:text-[#A85A4A]
                                        "
                                    >
                                        Clear
                                    </button>

                                </div>
                            )}

                        </div>

                        {/* ==================================================
                            LOADING
                        ================================================== */}

                        {loading ? (
                            <div
                                className="
                                    grid
                                    grid-cols-1
                                    gap-6

                                    md:grid-cols-2

                                    xl:grid-cols-3
                                "
                            >

                                {[1, 2, 3, 4, 5, 6].map(
                                    (item) => (
                                        <div
                                            key={item}
                                            className="
                                                overflow-hidden
                                                rounded-[22px]
                                                border
                                                border-[#DED8CC]
                                                bg-[#FBFAF6]
                                                shadow-[0_8px_30px_rgba(23,63,62,0.05)]
                                            "
                                        >

                                            <div
                                                className="
                                                    h-56
                                                    animate-pulse
                                                    bg-[#E7DED0]
                                                "
                                            />

                                            <div className="space-y-4 p-5">

                                                <div
                                                    className="
                                                        h-5
                                                        w-3/4
                                                        animate-pulse
                                                        rounded
                                                        bg-[#E7DED0]
                                                    "
                                                />

                                                <div
                                                    className="
                                                        h-3
                                                        w-1/2
                                                        animate-pulse
                                                        rounded
                                                        bg-[#F0ECE3]
                                                    "
                                                />

                                                <div
                                                    className="
                                                        h-12
                                                        w-full
                                                        animate-pulse
                                                        rounded-xl
                                                        bg-[#F0ECE3]
                                                    "
                                                />

                                                <div
                                                    className="
                                                        h-10
                                                        w-full
                                                        animate-pulse
                                                        rounded-xl
                                                        bg-[#E7DED0]
                                                    "
                                                />

                                            </div>
                                        </div>
                                    )
                                )}

                            </div>
                        ) : hotels.length === 0 ? (

                            /* ==================================================
                                EMPTY STATE
                            ================================================== */

                            <div
                                className="
                                    rounded-[24px]
                                    border
                                    border-[#DED8CC]
                                    bg-[#FBFAF6]
                                    px-6
                                    py-20
                                    text-center
                                    shadow-[0_10px_35px_rgba(23,63,62,0.05)]
                                "
                            >

                                <div
                                    className="
                                        mx-auto
                                        flex
                                        h-20
                                        w-20
                                        items-center
                                        justify-center
                                        rounded-full
                                        border
                                        border-[#D5A33C]/30
                                        bg-[#F3EBDD]
                                        text-3xl
                                        text-[#0B5552]
                                    "
                                >
                                    <FaHotel />
                                </div>

                                <h3
                                    className="
                                        mt-6
                                        font-serif
                                        text-2xl
                                        font-medium
                                        text-[#173F3E]
                                    "
                                >
                                    No hotels found
                                </h3>

                                <p
                                    className="
                                        mx-auto
                                        mt-2
                                        max-w-md
                                        text-sm
                                        leading-6
                                        text-[#687572]
                                    "
                                >
                                    We couldn't find any hotels
                                    matching your current filters.
                                    Try changing your search or
                                    resetting the filters.
                                </p>

                                <button
                                    onClick={resetFilters}
                                    className="
                                        mt-6
                                        inline-flex
                                        items-center
                                        gap-2
                                        rounded-[15px]
                                        bg-[#173F3E]
                                        px-6
                                        py-3.5
                                        text-sm
                                        font-bold
                                        text-white
                                        shadow-[0_8px_25px_rgba(23,63,62,0.18)]
                                        transition-all
                                        duration-300
                                        hover:-translate-y-0.5
                                        hover:bg-[#0B5552]
                                    "
                                >
                                    <FaUndo />

                                    Reset Filters
                                </button>

                            </div>

                        ) : (

                            /* ==================================================
                                HOTEL GRID
                            ================================================== */

                            <div
                                className="
                                    grid
                                    grid-cols-1
                                    gap-6

                                    md:grid-cols-2

                                    xl:grid-cols-3
                                "
                            >

                                {(limit
                                    ? hotels.slice(0, limit)
                                    : hotels
                                ).map((hotel) => (

                                    <article
                                        key={hotel.id}
                                        className="
                                            group
                                            flex
                                            flex-col
                                            overflow-hidden
                                            rounded-[22px]
                                            border
                                            border-[#DED8CC]
                                            bg-[#FBFAF6]
                                            shadow-[0_8px_35px_rgba(23,63,62,0.06)]
                                            transition-all
                                            duration-500
                                            hover:-translate-y-1
                                            hover:border-[#B78932]/40
                                            hover:shadow-[0_25px_60px_rgba(23,63,62,0.12)]
                                        "
                                    >

                                        {/* ==================================================
                                            IMAGE
                                        ================================================== */}

                                        <div
                                            className="
                                                relative
                                                h-[245px]
                                                cursor-pointer
                                                overflow-hidden
                                            "
                                            onClick={() =>
                                                navigate(
                                                    `/hotels/${hotel.slug}`
                                                )
                                            }
                                        >

                                            <img
                                                src={`https://uaediscountedhotels.com/uploads/hotels/${hotel.image}`}
                                                alt={hotel.name}
                                                className="
                                                    h-full
                                                    w-full
                                                    object-cover
                                                    transition-transform
                                                    duration-700
                                                    group-hover:scale-110
                                                "
                                            />

                                            {/* Image Overlay */}

                                            <div
                                                className="
                                                    absolute
                                                    inset-0
                                                    bg-gradient-to-t
                                                    from-[#173F3E]/90
                                                    via-[#173F3E]/10
                                                    to-transparent
                                                    opacity-90
                                                "
                                            />

                                            {/* Top Badge */}

                                            <div className="absolute left-4 top-4">

                                                <span
                                                    className="
                                                        inline-flex
                                                        items-center
                                                        gap-1.5
                                                        rounded-full
                                                        border
                                                        border-white/20
                                                        bg-[#173F3E]/85
                                                        px-3
                                                        py-1.5
                                                        text-[9px]
                                                        font-semibold
                                                        uppercase
                                                        tracking-[0.12em]
                                                        text-white
                                                        shadow-lg
                                                        backdrop-blur-md
                                                    "
                                                >

                                                    <FaHotel className="text-[#D5A33C]" />

                                                    Dubai Stay

                                                </span>

                                            </div>

                                            {/* Wishlist */}

                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();

                                                    toggleWishlist(
                                                        hotel
                                                    );
                                                }}
                                                className="
                                                    absolute
                                                    right-4
                                                    top-4
                                                    flex
                                                    h-11
                                                    w-11
                                                    items-center
                                                    justify-center
                                                    rounded-full
                                                    border
                                                    border-white/30
                                                    bg-white/90
                                                    text-[#687572]
                                                    shadow-lg
                                                    backdrop-blur-md
                                                    transition-all
                                                    duration-300
                                                    hover:scale-110
                                                    hover:bg-white
                                                    hover:text-[#173F3E]
                                                "
                                                aria-label="Add to wishlist"
                                            >

                                                {isWishlisted(
                                                    hotel.id
                                                ) ? (
                                                    <FaHeart className="text-[#A85A4A]" />
                                                ) : (
                                                    <FaRegHeart />
                                                )}

                                            </button>

                                            {/* Location */}

                                            <div className="absolute bottom-4 left-4">

                                                <div
                                                    className="
                                                        flex
                                                        items-center
                                                        gap-2
                                                        rounded-xl
                                                        border
                                                        border-white/30
                                                        bg-[#FBFAF6]/95
                                                        px-3
                                                        py-2
                                                        shadow-lg
                                                        backdrop-blur-md
                                                    "
                                                >

                                                    <FaMapMarkerAlt className="text-xs text-[#0B5552]" />

                                                    <span
                                                        className="
                                                            max-w-[150px]
                                                            truncate
                                                            text-[11px]
                                                            font-bold
                                                            text-[#173F3E]
                                                        "
                                                    >
                                                        {hotel.location ||
                                                            "Dubai, UAE"}
                                                    </span>

                                                </div>

                                            </div>

                                            {/* Rating */}

                                            <div className="absolute bottom-4 right-4">

                                                <div
                                                    className="
                                                        flex
                                                        items-center
                                                        gap-2
                                                        rounded-xl
                                                        bg-[#D5A33C]
                                                        px-3
                                                        py-2
                                                        text-[#173F3E]
                                                        shadow-lg
                                                    "
                                                >

                                                    <FaStar className="text-[11px]" />

                                                    <span className="text-xs font-black">
                                                        {hotel.rating ||
                                                            "4.5"}
                                                    </span>

                                                </div>

                                            </div>

                                        </div>

                                        {/* ==================================================
                                            CONTENT
                                        ================================================== */}

                                        <div
                                            className="
                                                flex
                                                flex-1
                                                flex-col
                                                p-5
                                            "
                                        >

                                            {/* Hotel Name */}

                                            <h3
                                                onClick={() =>
                                                    navigate(
                                                        `/hotels/${hotel.slug}`
                                                    )
                                                }
                                                className="
                                                    line-clamp-1
                                                    cursor-pointer
                                                    font-serif
                                                    text-xl
                                                    font-medium
                                                    text-[#173F3E]
                                                    transition-colors
                                                    duration-300
                                                    hover:text-[#0B5552]
                                                "
                                            >
                                                {hotel.name}
                                            </h3>

                                            {/* Stars */}

                                            <div className="mt-2 flex items-center gap-2">

                                                <div
                                                    className="
                                                        flex
                                                        gap-0.5
                                                        text-[11px]
                                                        text-[#D5A33C]
                                                    "
                                                >
                                                    {renderStars(
                                                        hotel.rating ||
                                                        4.5
                                                    )}
                                                </div>

                                                <span
                                                    className="
                                                        text-[10px]
                                                        font-medium
                                                        text-[#8A9490]
                                                    "
                                                >
                                                    Hotel rating
                                                </span>

                                            </div>

                                            {/* Description */}

                                            <p
                                                className="
                                                    mt-3
                                                    line-clamp-2
                                                    min-h-[42px]
                                                    text-xs
                                                    leading-6
                                                    text-[#687572]
                                                "
                                            >
                                                {hotel.short_description?.replace(
                                                    /<[^>]*>/g,
                                                    ""
                                                ) ||
                                                    "Enjoy a comfortable and memorable stay in one of Dubai's exceptional hotels."}
                                            </p>

                                            {/* Amenities */}

                                            <div
                                                className="
                                                    mt-4
                                                    grid
                                                    grid-cols-3
                                                    gap-2
                                                    border-y
                                                    border-[#DED8CC]
                                                    py-4
                                                "
                                            >

                                                {/* Pool */}

                                                <div
                                                    className="
                                                        flex
                                                        flex-col
                                                        items-center
                                                        gap-1.5
                                                        rounded-xl
                                                        bg-[#F3EBDD]
                                                        py-2.5
                                                        transition-colors
                                                        duration-300
                                                        group-hover:bg-[#F7F5EF]
                                                    "
                                                >

                                                    <FaSwimmingPool className="text-sm text-[#0B5552]" />

                                                    <span
                                                        className="
                                                            text-[9px]
                                                            font-semibold
                                                            text-[#687572]
                                                        "
                                                    >
                                                        Pool
                                                    </span>

                                                </div>

                                                {/* WiFi */}

                                                <div
                                                    className="
                                                        flex
                                                        flex-col
                                                        items-center
                                                        gap-1.5
                                                        rounded-xl
                                                        bg-[#F3EBDD]
                                                        py-2.5
                                                        transition-colors
                                                        duration-300
                                                        group-hover:bg-[#F7F5EF]
                                                    "
                                                >

                                                    <FaWifi className="text-sm text-[#0B5552]" />

                                                    <span
                                                        className="
                                                            text-[9px]
                                                            font-semibold
                                                            text-[#687572]
                                                        "
                                                    >
                                                        WiFi
                                                    </span>

                                                </div>

                                                {/* Restaurant */}

                                                <div
                                                    className="
                                                        flex
                                                        flex-col
                                                        items-center
                                                        gap-1.5
                                                        rounded-xl
                                                        bg-[#F3EBDD]
                                                        py-2.5
                                                        transition-colors
                                                        duration-300
                                                        group-hover:bg-[#F7F5EF]
                                                    "
                                                >

                                                    <FaUtensils className="text-sm text-[#0B5552]" />

                                                    <span
                                                        className="
                                                            text-[9px]
                                                            font-semibold
                                                            text-[#687572]
                                                        "
                                                    >
                                                        Restaurant
                                                    </span>

                                                </div>

                                            </div>

                                            {/* ==================================================
                                                BOTTOM
                                            ================================================== */}

                                            <div
                                                className="
                                                    mt-auto
                                                    flex
                                                    items-end
                                                    justify-between
                                                    gap-3
                                                    pt-5
                                                "
                                            >

                                                <div>

                                                    <p
                                                        className="
                                                            text-[9px]
                                                            font-semibold
                                                            uppercase
                                                            tracking-[0.12em]
                                                            text-[#8A9490]
                                                        "
                                                    >
                                                        Starting from
                                                    </p>

                                                    <div className="mt-0.5 flex items-baseline gap-1">

                                                        <span
                                                            className="
                                                                text-[10px]
                                                                font-bold
                                                                text-[#687572]
                                                            "
                                                        >
                                                            AED
                                                        </span>

                                                        <span
                                                            className="
                                                                font-serif
                                                                text-2xl
                                                                font-medium
                                                                text-[#173F3E]
                                                            "
                                                        >
                                                            {Math.round(
                                                                hotel.price_per_night
                                                            )}
                                                        </span>

                                                    </div>

                                                    <p
                                                        className="
                                                            text-[9px]
                                                            text-[#8A9490]
                                                        "
                                                    >
                                                        per night
                                                    </p>

                                                </div>

                                                {/* View Hotel */}

                                                <button
                                                    onClick={() =>
                                                        navigate(
                                                            `/hotels/${hotel.slug}`
                                                        )
                                                    }
                                                    className="
                                                        group/view
                                                        flex
                                                        items-center
                                                        gap-2
                                                        rounded-[13px]
                                                        bg-[#173F3E]
                                                        px-4
                                                        py-3
                                                        text-[11px]
                                                        font-bold
                                                        text-white
                                                        shadow-[0_8px_20px_rgba(23,63,62,0.16)]
                                                        transition-all
                                                        duration-300
                                                        hover:-translate-y-0.5
                                                        hover:bg-[#0B5552]
                                                    "
                                                >

                                                    View Hotel

                                                    <FaArrowRight
                                                        className="
                                                            text-[9px]
                                                            transition-transform
                                                            duration-300
                                                            group-hover/view:translate-x-1
                                                        "
                                                    />

                                                </button>

                                            </div>

                                        </div>

                                        {/* Gold Bottom Accent */}

                                        <div
                                            className="
                                                h-[2px]
                                                w-0
                                                bg-[#D5A33C]
                                                transition-all
                                                duration-500
                                                group-hover:w-full
                                            "
                                        />

                                    </article>

                                ))}

                            </div>
                        )}

                        {/* ==================================================
                            MOBILE VIEW ALL
                        ================================================== */}

                        {(showHeading || showViewAllButton) && (
                            <div className="mt-8 sm:hidden">

                                <button
                                    onClick={() =>
                                        navigate("/hotels")
                                    }
                                    className="
                                        flex
                                        w-full
                                        items-center
                                        justify-center
                                        gap-3
                                        rounded-[15px]
                                        bg-[#173F3E]
                                        py-4
                                        text-sm
                                        font-bold
                                        text-white
                                        shadow-[0_8px_25px_rgba(23,63,62,0.15)]
                                        transition-all
                                        duration-300
                                        hover:bg-[#0B5552]
                                    "
                                >

                                    View All Hotels

                                    <FaArrowRight className="text-xs" />

                                </button>

                            </div>
                        )}

                    </main>

                </div>
            </div>
        </div>
    );
}